/**
 * IMPORTS
 */

import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { CheckCircle, MessageCircleQuestion, PackageCheck } from "lucide-react";
import { toast } from "react-toastify";

import Avatar from "@components/avatar/avatar";
import { Button } from "@components/button/button";
import { ModalDetalhesRemessa } from "@components/modal-detalhes-remessa";
import { SelectPagination } from "@components/select-pagination/select-pagination";
import { Spinner } from "@components/spinner/spinner";
// components
import { DynamicTable } from "@components/table-custom/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/tabs";

import { BatchFinalizeModal } from "@pages/expedicao/dashboard/components/batch-finalize-modal/batch-finalize-modal";
import type { RemessaSelecionadaLote } from "@pages/expedicao/dashboard/components/batch-finalize-modal/interface";
import { ObservationRecepcaoModal } from "@pages/recepcao/components/observation-modal";
import { FinalizeRecepcaoModal } from "@pages/recepcao/components/recepcao-finalize-modal/recepcao-finalize-modal";
import { SearchInputRecepcao } from "@pages/recepcao/components/search-recepcao";
import { montarSecoesDetalhesRemessaRecepcao } from "@pages/recepcao/helpers/montar-secoes-detalhes-remessa-recepcao";

// hooks
import { useRemessasEmRecepcao } from "@/hooks/recepcao";

import { useStoreZustandPegarTarefas } from "@/store-zustand/admin/pegar-tarefas";
// zustand
import { useStoreZustandRecepcao } from "@/store-zustand/recepcao/recepcao";

import ImageAvatar from "@/common/assets/png/perfil-default-letscom.png";
import { FEATURES, useFeature } from "@/utils/auth/features";

const DashboardRecepcao = () => {
  const {
    currentPage,
    setCurrentPage,
    totalItemsPage,
    itemsPerPage,
    setItemsPerPage,
    lastPage,
    isLoading: isLoadingMainExpedicao,
    remessaUnicaEmRecepcao,
    setRemessaUnicaEmRecepcao,
  } = useStoreZustandRecepcao();

  const { remessasEmRecepcao, paginations, isLoading: isLoadingPage } = useRemessasEmRecepcao();

  const { setTarefa } = useStoreZustandPegarTarefas();
  const podeLiberarEmLote = useFeature(FEATURES.LIBERACAO_MULTIPLAS_REMESSAS_RECEPCAO);
  const podeLiberarRemessa = useFeature(FEATURES.LIBERACAO_REMESSAS_RECEPCAO);

  const [isModalObservacao, setIsModalObservacao] = useState(false);
  const [finalizeRecepcaoModal, setFinalizeRecepcaoModal] = useState(false);
  const [isModalBatchFinalize, setIsModalBatchFinalize] = useState(false);
  const [linhaDetalhesAberta, setLinhaDetalhesAberta] = useState<Record<string, any> | null>(null);
  const fecharModalDetalhes = useCallback(() => setLinhaDetalhesAberta(null), []);
  // Keep remessa objects (not only IDs) so lote modal works across pages
  const [selectedRemessasById, setSelectedRemessasById] = useState<
    Record<number, RemessaSelecionadaLote>
  >({});

  const selectedIds = useMemo(
    () => Object.keys(selectedRemessasById).map(Number),
    [selectedRemessasById]
  );

  const remessasSelecionadas = useMemo(
    () => Object.values(selectedRemessasById),
    [selectedRemessasById]
  );

  const handleSelectAll = useCallback(() => {
    const pageItems = remessasEmRecepcao ?? [];
    const pageIds = pageItems.map((item) => item.id);
    const allPageSelected = pageIds.every((id) => selectedRemessasById[id]);

    if (allPageSelected) {
      setSelectedRemessasById((prev) => {
        const next = { ...prev };
        pageIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      return;
    }

    setSelectedRemessasById((prev) => {
      const next = { ...prev };
      const currentCount = Object.keys(prev).length;
      const availableSlots = 10 - currentCount;
      const itemsToAdd = pageItems.slice(0, availableSlots);

      itemsToAdd.forEach((item) => {
        next[item.id] = item as RemessaSelecionadaLote;
      });

      // Avisa se havia mais itens que não couberam
      if (pageItems.length > availableSlots) {
        const notAddedCount = pageItems.length - availableSlots;
        toast.info(
          `${notAddedCount} remessa(s) não selecionada(s) — limite de 10 por envio atingido.`
        );
      }

      return next;
    });
  }, [remessasEmRecepcao, selectedRemessasById]);

  const handleSelectItem = useCallback(
    (id: number) => {
      setSelectedRemessasById((prev) => {
        if (prev[id]) {
          const next = { ...prev };
          delete next[id];
          return next;
        }

        // Guard: máximo de 10 remessas por envio
        if (Object.keys(prev).length >= 10) {
          toast.warning("Máximo de 10 remessas por envio. Libere estas e selecione as próximas.");
          return prev;
        }

        const item = (remessasEmRecepcao ?? []).find((remessa) => remessa.id === id);
        if (!item) return prev;

        return { ...prev, [id]: item as RemessaSelecionadaLote };
      });
    },
    [remessasEmRecepcao]
  );

  const handleBatchSuccess = useCallback(() => {
    setSelectedRemessasById({});
    setIsModalBatchFinalize(false);
  }, []);

  // Função para lidar com a alteração do seletor de itens por página
  const handleItemsPerPageChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      setItemsPerPage(Number(event.target.value));
    },
    [setItemsPerPage]
  );

  const onGoToPage = useCallback(
    (action: number | "prev" | "next") => {
      const newPage =
        action === "prev"
          ? Math.max(1, currentPage - 1)
          : action === "next"
            ? currentPage + 1
            : action;

      setCurrentPage(newPage);
    },
    [currentPage, setCurrentPage]
  );

  return (
    <>
      {isLoadingMainExpedicao ? (
        <Spinner />
      ) : (
        <>
          <Tabs defaultValue="all">
            <div className="mb-4 flex items-center">
              <TabsList>
                <TabsTrigger value="all">Recepção</TabsTrigger>
              </TabsList>
              <div className="ml-auto flex items-center gap-2">
                <div className="hidden md:block">
                  <SelectPagination
                    itemsPerPage={itemsPerPage}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>

                <div className="flex justify-end p-4">
                  <Avatar
                    alt="Avatar do usuário"
                    size="small"
                    name="avat-perfil"
                    src={ImageAvatar}
                  />
                </div>
              </div>
            </div>

            {podeLiberarEmLote && selectedIds.length > 0 && (
              <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                <span className="text-sm font-medium text-blue-800">
                  {selectedIds.length} remessa(s) selecionada(s)
                </span>
                <Button
                  type="button"
                  className="flex items-center gap-2"
                  onClick={() => setIsModalBatchFinalize(true)}
                >
                  <PackageCheck className="size-4" />
                  Liberar Selecionadas
                </Button>
              </div>
            )}

            <TabsContent value="all">
              <DynamicTable
                title="Recepção"
                description="Gerencie remessas da recepção e visualize quando quiser."
                isLoadingPage={isLoadingPage}
                data={remessasEmRecepcao ?? []}
                totalItems={totalItemsPage}
                itemsPerPage={itemsPerPage}
                checkBox={podeLiberarEmLote}
                selectedItems={selectedIds}
                onSelectAll={handleSelectAll}
                onSelectItem={handleSelectItem}
                onRowClick={setLinhaDetalhesAberta}
                columns={[
                  { label: "Remessa", accessor: "numero_remessa" },
                  { label: "Cliente", accessor: "cliente_name", limit: 60 },
                  {
                    label: "Modelo",
                    accessor: "modelo",
                    limit: 60,
                    customRender: (value: any, item: any) => (
                      <Link
                        to={`/visualizar-modelo`}
                        className="font-medium text-blue-600 transition-colors duration-200 hover:text-blue-800 hover:underline"
                        onClick={() => {
                          setRemessaUnicaEmRecepcao(item);
                          setTarefa(item);
                        }}
                      >
                        {value}
                      </Link>
                    ),
                  },
                  { label: "Qtde", accessor: "quantidadeSolicitacao" },
                  { label: "Furo", accessor: "furo", isBadge: true },
                  { label: "Prazo", accessor: "prazo", isBadge: true },
                  { label: "Responsável", accessor: "responsavel", limit: 60 },
                  { label: "Tecnologia", accessor: "tecnologia" },
                  { label: "Data Solicitação", accessor: "data" },
                  { label: "E-mail", accessor: "email", limit: 150 },
                ]}
                onGoToPage={onGoToPage}
                paginations={paginations}
                currentPage={currentPage}
                lastPage={lastPage}
                actions={[
                  {
                    icon: CheckCircle,
                    label: "Concluir expedição",
                    textColor: "text-red-600",
                    visible: podeLiberarRemessa,
                    onClick: (item: any) => {
                      setFinalizeRecepcaoModal(true);
                      setRemessaUnicaEmRecepcao(item);
                    },
                  },

                  {
                    icon: MessageCircleQuestion,
                    label: "Observação",
                    iconClassName: (item: any) =>
                      item.hasMensagem ? "h-4 w-4 text-yellow-500" : "h-4 w-4 text-zinc-900",
                    onClick: (item: any) => {
                      setIsModalObservacao(true);
                      setRemessaUnicaEmRecepcao(item);
                    },
                  },
                ]}
                childerSearch={<SearchInputRecepcao />}
              />
            </TabsContent>
          </Tabs>

          {linhaDetalhesAberta && (
            <ModalDetalhesRemessa
              titulo={`Remessa ${linhaDetalhesAberta.numero_remessa}`}
              subtitulo={linhaDetalhesAberta.cliente}
              secoes={montarSecoesDetalhesRemessaRecepcao(linhaDetalhesAberta)}
              onFechar={fecharModalDetalhes}
            />
          )}

          {isModalObservacao && remessaUnicaEmRecepcao?.remessa && (
            <ObservationRecepcaoModal
              handleToggleCreateActvityModal={() => {
                setIsModalObservacao(false);
              }}
              remessa={remessaUnicaEmRecepcao! || null}
            />
          )}

          {podeLiberarRemessa && finalizeRecepcaoModal && remessaUnicaEmRecepcao?.remessa && (
            <FinalizeRecepcaoModal
              handleClose={() => setFinalizeRecepcaoModal(false)}
              remessa={remessaUnicaEmRecepcao as any}
            />
          )}

          {podeLiberarEmLote && isModalBatchFinalize && remessasSelecionadas.length > 0 && (
            <BatchFinalizeModal
              modulo="recepcao"
              remessas={remessasSelecionadas}
              handleClose={() => setIsModalBatchFinalize(false)}
              onSuccess={handleBatchSuccess}
            />
          )}
        </>
      )}
    </>
  );
};

/**
 * EXPORTS
 */
export { DashboardRecepcao };
